import { FormattedDuration } from '@10xLabs/react-web/components/FormattedDuration'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { FormattedDistance } from 'components/FormattedDistance'
import { AngleDownIcon } from 'components/Icons/AngleDown'
import { ClockIcon } from 'components/Icons/ClockIcon'
import { ReadinessScoreIcon } from 'components/Icons/ReadinessScoreIcon'
import {
  IDraggedLocation,
  ILocationRemove,
  IRoutingProjectPlan,
  IRoutingProjectPlanModel
} from 'features/RoutingProjects/types'
import { getUniqueColor } from 'utils/style'
import { RouteStops } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList/StopsList'
import { RouteActions } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList/RouteItem/Actions'
import { AddStopModal } from 'features/RoutingProjects/components/AddStopModal'
import { VisibilityOffOutlineIcon } from 'components/Icons/VisibilityOffOutlineIcon'
import { VisibilityOutlineIcon } from 'components/Icons/VisibilityOutlineIcon'

const RouteItemContainer = styled.li`
  display: grid;
  grid-template-columns: 7px 1fr;
  border: 1px solid #eae9e9;
  border-radius: 8px;
`

const RouteItemFlag = styled.div<{ color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ color }): string => color};
  border-radius: 22px 0 0 22px;
`

const RouteItemTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content min-content min-content;
  align-items: center;
  padding: 16px;
  gap: 8px;
  cursor: pointer;
`

const RouteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const VisibilityContainer = styled.span`
  display: flex;
  cursor: pointer;
  padding: 16px 8px;
  margin: -16px -8px;
`

const RouteDataContainer = styled.div`
  display: flex;
  gap: 8px;
`

const RouteData = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #717171;
`

const RouteDataIcon = styled.div`
  display: flex;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    fill: #423f9a;
  }
`

const RouteName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
`

const StyledAngleDownIcon = styled(AngleDownIcon)<{ isOpen: boolean }>`
  fill: #717171;
  height: 6px !important;
  transform: ${({ isOpen }): string => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: 0.2s;
  width: 10px !important;
  margin: 0 2px;
`

const Split = styled.div`
  width: calc(100% - 32px);
  margin: auto;
  background-color: #eae9e9;
  height: 1px;
`

const RouteStopsContainer = styled.div`
  padding: 16px;
`

interface IRouteItemProps {
  routingProjectId: string
  route: IRoutingProjectPlan['optimization']['routes'][0]
  routeIndex: number
  draggedLocation: IDraggedLocation | null
  onLocationRemove?: (args: ILocationRemove) => void
  planModel: IRoutingProjectPlanModel
  setPlanModel: (planModel: IRoutingProjectPlanModel) => void
  visibleRoutesIds: string[]
  setVisibleRoutesIds: (visibleRoutesIds: string[]) => void
}

export const RouteItem = ({
  routingProjectId,
  route,
  routeIndex,
  draggedLocation,
  onLocationRemove,
  planModel,
  setPlanModel,
  visibleRoutesIds,
  setVisibleRoutesIds
}: IRouteItemProps): ReactElement => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [isOpenAddStopModal, setIsOpenAddStopModal] = useState(false)

  const rgbColor = getUniqueColor(routeIndex)
  const isRouteVisible = visibleRoutesIds?.includes(route.id)

  const toggleRouteDetails = (): void => {
    setIsDetailsOpen(!isDetailsOpen)
  }

  const handleToggleVisibility = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    const updatedVisibleRoutesIds = isRouteVisible
      ? visibleRoutesIds.filter((routeId) => routeId !== route.id)
      : [...visibleRoutesIds, route.id]
    setVisibleRoutesIds(updatedVisibleRoutesIds)
  }

  return (
    <RouteItemContainer>
      <RouteItemFlag color={rgbColor} />
      <div>
        <RouteItemTitle onClick={toggleRouteDetails}>
          <RouteDetails>
            <RouteName>
              <FormattedMessage
                id="routingProjectSidebar.optimizedRoutesList.routeName"
                defaultMessage="Route {routeIndex}"
                values={{ routeIndex: routeIndex + 1 }}
              />
            </RouteName>
            <RouteDataContainer>
              <RouteData>
                <RouteDataIcon>
                  <ReadinessScoreIcon />
                </RouteDataIcon>
                <FormattedDistance distance={route.distance} decimals={2} />
              </RouteData>
              <RouteData>
                <RouteDataIcon>
                  <ClockIcon />
                </RouteDataIcon>
                <FormattedDuration duration={route.duration} />
              </RouteData>
            </RouteDataContainer>
          </RouteDetails>
          <VisibilityContainer onClick={handleToggleVisibility}>
            {isRouteVisible ? <VisibilityOutlineIcon /> : <VisibilityOffOutlineIcon />}
          </VisibilityContainer>
          <RouteActions setIsOpenAddStopModal={setIsOpenAddStopModal} />
          <StyledAngleDownIcon isOpen={isDetailsOpen} />
        </RouteItemTitle>
        {isDetailsOpen && (
          <>
            <Split />
            <RouteStopsContainer>
              <RouteStops
                routeIndex={routeIndex}
                stops={route.stops}
                rgbColor={rgbColor}
                draggedLocation={draggedLocation}
                onLocationRemove={onLocationRemove}
                planModel={planModel}
              />
            </RouteStopsContainer>
          </>
        )}
      </div>
      {isOpenAddStopModal && (
        <AddStopModal
          routingProjectId={routingProjectId}
          planModel={planModel}
          routeIndex={routeIndex}
          setPlanModel={setPlanModel}
          closeModal={(): void => setIsOpenAddStopModal(false)}
        />
      )}
    </RouteItemContainer>
  )
}
